// Fuel-tank Excel import — parses the warehouse "Fuel Tank Serial Number" workbook and maps
// its rows into the vessel-centric fuel-tank model used by the Fuel Tank — S/N page.
//
// The workbook is organised by tank product: one sheet per model (GT7, GTX, A50…), each with
// one or more blocks (a "Sap:" material code + a table of physical tanks). Every tank row may
// name the vessel it was fitted to in the "Σκάφος" column. We import exactly those vessel-bound
// rows, matching Σκάφος against the overview vessels (by SAP or name) and appending a tank per
// match. Rows with no vessel (stock), or whose vessel isn't in the current overview, are surfaced
// as skipped so the import is transparent rather than silent.
//
// The parsing (parseFuelTankWorkbook) is a thin wrapper over SheetJS; the mapping (planImport)
// is pure and unit-tested.

/* eslint-disable @typescript-eslint/no-explicit-any */

export const MAX_TANKS = 20;

// ── parsed workbook shapes ────────────────────────────────────────────────────
export interface ParsedTankRow {
  sn: string; // serial-within-model, e.g. '003' (may be a placeholder like 'NO S/N')
  receipt: string; // receipt date, ISO or ''
  floatInstalled: string; // "Φλοτέρ τοποθετημένο" - float part / installed marker
  install: string; // installation date, ISO or the raw cell text
  vessel: string; // "Σκάφος" - the vessel the tank was fitted to (SAP or name), or ''
  comment: string; // "Σχόλιο"
}
export interface ParsedBlock {
  sap: string; // material SAP of this tank product
  description: string; // optional descriptive label next to the SAP
  rows: ParsedTankRow[];
}
export interface ParsedSheet {
  name: string; // tank model - the sheet name (GT7, GTX, A50…)
  blocks: ParsedBlock[];
}

// ── date helpers (mirror the Vessel Status importer) ──────────────────────────
const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
const pad2 = (n: number) => String(n).padStart(2, '0');
const isoOf = (d: Date) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
export const serialToISO = (n: number) => isoOf(new Date(EXCEL_EPOCH + Math.round(n) * 86400000));

/** display text of a SheetJS cell — dates become ISO, everything else its formatted/raw string. */
function cellText(cell: any): string {
  if (cell == null) return '';
  if (cell.v instanceof Date) return isoOf(cell.v);
  if (cell.t === 'n') {
    const nf: string = typeof cell.z === 'string' ? cell.z : '';
    const isDate = /[dmy]/i.test(nf) && !/[h]/i.test(nf);
    if (isDate) return serialToISO(cell.v);
    return String(cell.v);
  }
  const s = cell.w ?? cell.v ?? '';
  return String(s).trim();
}

// header matchers use accent-free stems (the sheet accents έ/ό/ά inconsistently).
const HDR_SN = 's/n';
const HDR_RECEIPT = /παραλαβ/i; // Ημερ.Παραλαβής
const HDR_FLOAT = /φλοτ/i; // Φλοτέρ τοποθετημένο
const HDR_INSTALL = /τοποθ/i; // Ημερ.Τοποθέτησης (stem before the accented έ)
const HDR_VESSEL = /σκάφος|σκαφος/i; // Σκάφος
const HDR_COMMENT = /σχόλ|σχολ/i; // Σχόλιο
const isSapLabel = (s: string) => /^sap\b/i.test(s.trim());

/** Parse the fuel-tank workbook into sheets → blocks → rows. `XLSX` is the SheetJS module. */
export function parseFuelTankWorkbook(XLSX: any, buf: ArrayBuffer): ParsedSheet[] {
  const wb = XLSX.read(buf, { type: 'array', cellNF: true });
  const out: ParsedSheet[] = [];
  for (const name of wb.SheetNames as string[]) {
    const ws = wb.Sheets[name];
    if (!ws || !ws['!ref']) continue;
    const ref = XLSX.utils.decode_range(ws['!ref']);
    const at = (r: number, c: number) => cellText(ws[XLSX.utils.encode_cell({ r, c })]);

    // locate the header row (the one carrying an "S/N" column); default to the second row.
    let hdrRow = -1;
    for (let r = ref.s.r; r <= Math.min(ref.e.r, ref.s.r + 4); r++) {
      for (let c = ref.s.c; c <= ref.e.c; c++) {
        if (at(r, c).trim().toLowerCase() === HDR_SN) { hdrRow = r; break; }
      }
      if (hdrRow >= 0) break;
    }
    if (hdrRow < 0) continue; // not a recognisable tank sheet
    const sapRow = Math.max(ref.s.r, hdrRow - 1);

    // each "S/N" header column starts a block; read its sibling columns by header text.
    const blocks: ParsedBlock[] = [];
    for (let c = ref.s.c; c <= ref.e.c; c++) {
      if (at(hdrRow, c).trim().toLowerCase() !== HDR_SN) continue;
      const snCol = c;
      // sibling columns sit at fixed offsets from S/N; take one only if its header matches.
      const receiptCol = c > ref.s.c && HDR_RECEIPT.test(at(hdrRow, c - 1)) ? c - 1 : -1;
      const floatCol = HDR_FLOAT.test(at(hdrRow, c + 1)) ? c + 1 : -1;
      const installCol = HDR_INSTALL.test(at(hdrRow, c + 2)) ? c + 2 : -1;
      const vesselCol = HDR_VESSEL.test(at(hdrRow, c + 3)) ? c + 3 : -1;
      const commentCol = HDR_COMMENT.test(at(hdrRow, c + 4)) ? c + 4 : -1;

      // block SAP sits in the "Sap:" row directly above the S/N column (label + value).
      let sap = at(sapRow, snCol).trim();
      if ((isSapLabel(sap) || !sap) && snCol > ref.s.c) sap = at(sapRow, snCol - 1).trim();
      if (isSapLabel(sap)) sap = '';
      // a human label may follow the SAP (e.g. "ΔΕΞΑΜΕΝΗ ΚΑΥΣΙΜΟΥ …") — capture the first one.
      let description = '';
      for (let d = snCol + 1; d <= Math.min(ref.e.c, snCol + 4); d++) {
        const t = at(sapRow, d).trim();
        if (t && !isSapLabel(t) && !/^\d+$/.test(t)) { description = t; break; }
      }

      const rows: ParsedTankRow[] = [];
      for (let r = hdrRow + 1; r <= ref.e.r; r++) {
        const sn = at(r, snCol).trim();
        const receipt = receiptCol >= 0 ? at(r, receiptCol).trim() : '';
        const floatInstalled = floatCol >= 0 ? at(r, floatCol).trim() : '';
        const install = installCol >= 0 ? at(r, installCol).trim() : '';
        const vessel = vesselCol >= 0 ? at(r, vesselCol).trim() : '';
        const comment = commentCol >= 0 ? at(r, commentCol).trim() : '';
        if (!sn && !receipt && !vessel && !comment && !install) continue; // blank row
        rows.push({ sn, receipt, floatInstalled, install, vessel, comment });
      }
      blocks.push({ sap, description, rows });
    }
    if (blocks.length) out.push({ name, blocks });
  }
  return out;
}

// ── mapping into the per-vessel model ─────────────────────────────────────────
export interface OverviewVesselLite {
  sap: string;
  name: string;
  model?: string;
}
export interface ExistingTankLite {
  tankNo: number;
  serial: string;
}

export type SkipReason = 'unmatched' | 'duplicate' | 'overflow';

export interface PlannedImport {
  sap: string; // target vessel SAP (the storage key)
  tankNo: number; // provisional number for the preview only - the server allocates the real one
  serial: string; // e.g. 'GT7 003'
  receiptDate: string; // Ημερ.Παραλαβής
  floatInstalled: string; // Φλοτέρ τοποθετημένο
  installDate: string; // Ημερ.Τοποθέτησης
  note: string; // Σχόλιο (comment)
  vesselName: string;
  model: string; // source sheet / tank model
  sn: string;
  matchedOn: 'sap' | 'name';
}
export interface SkippedRow {
  vessel: string; // raw Σκάφος
  vesselName?: string; // resolved name, when matched but skipped
  model: string;
  sn: string;
  serial: string;
  reason: SkipReason;
}
export interface ImportPlan {
  imports: PlannedImport[];
  skipped: SkippedRow[];
  stats: {
    sheets: number;
    rowsWithVessel: number;
    toImport: number;
    duplicates: number;
    unmatched: number;
    overflow: number;
    vesselsAffected: number;
  };
}

const normKey = (s: string) => s.trim().toUpperCase().replace(/\s+/g, '');

// digit "core" of a vessel SAP. The workbook's Σκάφος column holds the vessel SAP as "J9100874"
// (or "JS9100562"); the overview may store the same SAP with or without that prefix and with or
// without leading zeros. Reducing both sides to their trailing digits lets them match regardless.
// Returns '' for anything that isn't a J/JS-prefixed or numeric SAP (e.g. a text vessel name).
const sapCore = (s: string): string => {
  const core = normKey(s).replace(/^JS?/, '').replace(/^0+/, '');
  return /^\d{4,}$/.test(core) ? core : '';
};

/** the serial we store: "<model> <s/n>", trimmed to the field limit. */
export function buildSerial(model: string, sn: string): string {
  const s = sn.trim();
  return `${model.trim()}${s ? ` ${s}` : ''}`.trim().slice(0, 80);
}

/**
 * Map parsed sheets onto the vessel-centric model. Only rows naming a vessel are considered;
 * each is matched to an overview vessel by exact SAP or name, then by SAP digit-core (so the
 * workbook's "J9100874" matches whatever prefix/zero-padding the overview stores). Duplicate
 * serials (already on the vessel or earlier in this import) are skipped; vessels over MAX_TANKS overflow.
 */
export function planImport(
  sheets: ParsedSheet[],
  vessels: OverviewVesselLite[],
  existingBySap: Map<string, ExistingTankLite[]>,
): ImportPlan {
  const byKey = new Map<string, OverviewVesselLite>(); // exact (normalised) SAP or name
  const byCore = new Map<string, OverviewVesselLite>(); // SAP digit-core, for prefix/zero tolerance
  for (const v of vessels) {
    if (v.sap) { const k = normKey(v.sap); if (!byKey.has(k)) byKey.set(k, v); }
    if (v.name) { const k = normKey(v.name); if (!byKey.has(k)) byKey.set(k, v); }
    const c = v.sap ? sapCore(v.sap) : '';
    if (c && !byCore.has(c)) byCore.set(c, v);
  }

  const imports: PlannedImport[] = [];
  const skipped: SkippedRow[] = [];
  const nextNo = new Map<string, number>(); // vessel sap → next free tank number
  const seen = new Map<string, Set<string>>(); // vessel sap → normalised serials present
  const affected = new Set<string>();
  let rowsWithVessel = 0;

  const ensure = (sap: string) => {
    if (nextNo.has(sap)) return;
    const existing = existingBySap.get(sap) ?? [];
    nextNo.set(sap, existing.reduce((m, t) => Math.max(m, t.tankNo), 0) + 1);
    seen.set(sap, new Set(existing.map((t) => normKey(t.serial)).filter(Boolean)));
  };

  for (const sheet of sheets) {
    for (const block of sheet.blocks) {
      for (const row of block.rows) {
        const rawVessel = row.vessel.trim();
        if (!rawVessel) continue; // stock tank - nothing to attach it to
        rowsWithVessel++;
        const serial = buildSerial(sheet.name, row.sn);
        const key = normKey(rawVessel);
        // exact SAP/name match first; fall back to the SAP digit-core (tolerates the J/JS prefix).
        let vessel = byKey.get(key);
        let matchedOn: 'sap' | 'name' = vessel ? (normKey(vessel.sap) === key ? 'sap' : 'name') : 'sap';
        if (!vessel) {
          const c = sapCore(rawVessel);
          if (c) vessel = byCore.get(c);
        }
        if (!vessel) {
          skipped.push({ vessel: rawVessel, model: sheet.name, sn: row.sn, serial, reason: 'unmatched' });
          continue;
        }
        ensure(vessel.sap);
        const serials = seen.get(vessel.sap)!;
        const nSerial = normKey(serial);
        // only rows carrying a real S/N are idempotent-dedupable; two distinct tanks that both lack
        // an S/N share a model-only serial, so we must NOT collapse them into one.
        const dedupe = row.sn.trim() !== '';
        if (dedupe && nSerial && serials.has(nSerial)) {
          skipped.push({ vessel: rawVessel, vesselName: vessel.name, model: sheet.name, sn: row.sn, serial, reason: 'duplicate' });
          continue;
        }
        const tankNo = nextNo.get(vessel.sap)!;
        if (tankNo > MAX_TANKS) {
          skipped.push({ vessel: rawVessel, vesselName: vessel.name, model: sheet.name, sn: row.sn, serial, reason: 'overflow' });
          continue;
        }
        imports.push({
          sap: vessel.sap,
          tankNo,
          serial,
          receiptDate: row.receipt,
          floatInstalled: row.floatInstalled,
          installDate: row.install,
          note: row.comment.slice(0, 200),
          vesselName: vessel.name,
          model: sheet.name,
          sn: row.sn,
          matchedOn,
        });
        if (dedupe && nSerial) serials.add(nSerial);
        nextNo.set(vessel.sap, tankNo + 1);
        affected.add(vessel.sap);
      }
    }
  }

  return {
    imports,
    skipped,
    stats: {
      sheets: sheets.length,
      rowsWithVessel,
      toImport: imports.length,
      duplicates: skipped.filter((s) => s.reason === 'duplicate').length,
      unmatched: skipped.filter((s) => s.reason === 'unmatched').length,
      overflow: skipped.filter((s) => s.reason === 'overflow').length,
      vesselsAffected: affected.size,
    },
  };
}
